/**
 * Date helpers — kept tiny and dependency-free so the web bundle doesn't pull
 * in dayjs/date-fns just for "today" math and branding-driven formatting.
 * Server-side date logic still lives in `packages/api`.
 *
 * ===========================================================================
 * WHICH ONE DO I WANT? This choice is the whole bug, so read the two lines.
 *
 *   `ymdInZone(tz)`  — the CALENDAR DAY. Reach for this by default, via
 *                      `useCompanyToday()` / `useCompanyTimeZone()`
 *                      (hooks/useFormatDate.ts), which bind it to the tenant's
 *                      configured `general.timezone`.
 *   `ymdLocal()`     — the DEVICE's day. Only for something that is genuinely
 *                      about this machine's clock (a filename stamp, a
 *                      "downloaded at" note). Never for "today".
 *
 * There are three possible bases for "what day is it", and only one is right:
 *
 *   UTC (`new Date().toISOString().slice(0, 10)`) — wrong. UTC is still on
 *     yesterday for the first four hours of every Gulf day, so between local
 *     midnight and 04:00 the app was a day behind, every day.
 *
 *   The browser's zone (`ymdLocal()`) — wrong too, and more insidiously,
 *     because it is right by COINCIDENCE whenever the reader happens to be
 *     sitting in the same zone as the company. It is silently wrong for a
 *     roaming admin (this product has a per-user flag for exactly that), an
 *     HQ user abroad, or a laptop whose clock travelled with its owner.
 *
 *   The company's configured zone (`general.timezone`, default `Asia/Dubai`,
 *     flagged "Affects pay") — RIGHT, because it is the zone the numbers were
 *     computed in. Every server job that decides what day it is reads it
 *     (markAbsences, autoPunchOut, lateArrival, leaveAccrual, documentExpiry,
 *     scheduledReports…, all via `safeTimeZone` in
 *     packages/api/src/utils/timezone.ts). A client that picks a different
 *     basis is not merely off by a day — it is the product contradicting
 *     itself about one fact, which is what design-rules §3.1/§3.2 forbid.
 * ===========================================================================
 */

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Company-wide default IANA zone, mirroring `DEFAULT_TIME_ZONE` in
 * packages/api/src/utils/timezone.ts and the `general.timezone` schema default.
 * The client falls back to the same zone the server does, so an unreadable or
 * not-yet-loaded setting still leaves the two halves agreeing.
 */
export const DEFAULT_TIME_ZONE = 'Asia/Dubai';

/**
 * Return `timeZone` if `Intl` can resolve it, else `fallback`. The browser twin
 * of the API's `safeTimeZone`, and for the same reason: a legacy free-text zone
 * ("GMT+4", "Dubai", "") makes every `Intl.DateTimeFormat` below throw a
 * `RangeError`, which here would blank a whole screen rather than shift a day.
 */
export function safeTimeZone(
  timeZone: string | null | undefined,
  fallback: string = DEFAULT_TIME_ZONE
): string {
  if (timeZone) {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone });
      return timeZone;
    } catch {
      /* fall through to the default */
    }
  }
  return fallback;
}

/**
 * Format `date` as `YYYY-MM-DD` as the calendar reads it in `timeZone`.
 *
 * This is the client's answer to "what day is it", and the only one that agrees
 * with the server's rollups. `formatToParts` rather than `toLocaleDateString`
 * so the result cannot be reshaped by a locale or by a browser that decorates
 * the string (RTL marks around the digits are a real hazard here — the app runs
 * in Arabic). DST is handled by `Intl`; the Gulf has none, but Europe/Istanbul
 * is in the tenant picker and a hand-rolled offset would be wrong twice a year.
 */
export function ymdInZone(
  timeZone: string | null | undefined,
  date: Date = new Date()
): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: safeTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: 'year' | 'month' | 'day'): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** The `YYYY-MM` month `date` falls in, as the calendar reads it in `timeZone`. */
export function ymInZone(
  timeZone: string | null | undefined,
  date: Date = new Date()
): string {
  return ymdInZone(timeZone, date).slice(0, 7);
}

/** The calendar year `date` falls in, as the calendar reads it in `timeZone`. */
export function yearInZone(
  timeZone: string | null | undefined,
  date: Date = new Date()
): number {
  return Number(ymdInZone(timeZone, date).slice(0, 4));
}

/**
 * The calendar day `date` falls on in `timeZone`, as a `[year, month, day]`
 * triple with month 1-12.
 *
 * The entry point for range arithmetic: get the triple once, in the company's
 * zone, then do the month/day maths on bare integers through `ymdFromParts`,
 * where no zone is left to shift anything.
 */
export function partsInZone(
  timeZone: string | null | undefined,
  date: Date = new Date()
): [number, number, number] {
  return ymdInZone(timeZone, date).split('-').map(Number) as [number, number, number];
}

/**
 * A bare `Y/M/D` triple as `YYYY-MM-DD`, normalised through `Date.UTC`.
 *
 * The month is 1-12 and BOTH may overflow deliberately — `(y, m + 1, 0)` is the
 * last day of month `m`, `(y, m, d - 29)` is twenty-nine days back across as
 * many month boundaries as it takes. UTC because the triple has already had its
 * zone question answered by `partsInZone`; running the arithmetic in a zone
 * again is what lets a DST boundary move a range edge.
 */
export function ymdFromParts(y: number, m: number, d: number): string {
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

/**
 * `n` days after (or before, for a negative `n`) the calendar day `date` falls
 * on in `timeZone` — counted in DAYS, not in 24-hour blocks, so a DST boundary
 * cannot lose or gain one. Used for "default to 30 days out" style seeds.
 */
export function addDaysInZone(
  timeZone: string | null | undefined,
  days: number,
  date: Date = new Date()
): string {
  const [y, m, d] = ymdInZone(timeZone, date).split('-').map(Number) as [
    number,
    number,
    number,
  ];
  // UTC arithmetic on a bare calendar triple: no zone is involved any more, so
  // there is nothing left for an offset to shift.
  const shifted = new Date(Date.UTC(y, m - 1, d + days));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(
    shifted.getUTCDate()
  )}`;
}

/**
 * Format `date` as `YYYY-MM-DD` in the BROWSER's timezone.
 *
 * Not the default — see the header. Correct only for things that are genuinely
 * about this device's clock; for "today", use `ymdInZone` with the company zone.
 */
export function ymdLocal(date: Date = new Date()): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * The current calendar year in the browser's timezone. Device-local — prefer
 * `yearInZone(companyTimeZone)` for anything a reader interprets as "this year".
 */
export function currentYearLocal(): number {
  return new Date().getFullYear();
}


type SupportedPattern =
  | 'DD-MM-YYYY'
  | 'DD/MM/YYYY'
  | 'MM-DD-YYYY'
  | 'MM/DD/YYYY'
  | 'YYYY-MM-DD';

/**
 * Format a Date / ISO string with a branding-driven pattern. Unknown
 * patterns fall back to ISO (`YYYY-MM-DD`). Null / invalid input
 * returns an empty string so callers can safely interpolate the
 * result without guarding for NaN.
 */
export function formatDateWithPattern(
  value: Date | string | null | undefined,
  pattern: string = 'DD-MM-YYYY'
): string {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const yyyy = String(date.getFullYear());
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());

  switch (pattern as SupportedPattern) {
    case 'DD-MM-YYYY':
      return `${dd}-${mm}-${yyyy}`;
    case 'DD/MM/YYYY':
      return `${dd}/${mm}/${yyyy}`;
    case 'MM-DD-YYYY':
      return `${mm}-${dd}-${yyyy}`;
    case 'MM/DD/YYYY':
      return `${mm}/${dd}/${yyyy}`;
    case 'YYYY-MM-DD':
      return `${yyyy}-${mm}-${dd}`;
    default:
      return `${yyyy}-${mm}-${dd}`;
  }
}

/**
 * ONE CLOCK RENDERING FOR THE WHOLE PRODUCT.
 *
 * Every wall-clock time a reader sees comes through here, so a punch cannot
 * read `13:05` on one screen and `01:05 PM` on the next. Two things decide the
 * look and neither is the device:
 *
 *  - `hour12` is the tenant's General → time format setting ('12h' / '24h'),
 *    the same one Settings writes and the one every screen must agree with. A
 *    bare `toLocaleTimeString()` reads the hour cycle off the BROWSER, which is
 *    how the two looks got mixed in the first place.
 *  - `timeZone` is the company's zone, never the reader's — see the header of
 *    this file and docs/time-and-dates.md. A punch is an instant; which wall
 *    clock it is named against is a company fact.
 *
 * Two-digit minutes, and seconds only where a screen genuinely records to the
 * second (`seconds: true` — the audit log, and nothing else so far): a punch is
 * recorded to the minute everywhere else in the product, and a stray `:07` is a
 * precision claim. The hour CYCLE is the tenant's either way, which is the part
 * that must not differ between two screens.
 *
 * `''` for null/invalid input, same contract as `formatDateWithPattern`, so a
 * caller can interpolate the result without guarding for NaN — and can pick its
 * own dash for "nothing recorded" rather than inheriting one.
 */
export function formatClockTime(
  value: Date | string | number | null | undefined,
  opts: { timeZone: string; hour12: boolean; seconds?: boolean }
): string {
  if (value === null || value === undefined || value === '') return '';
  const date =
    value instanceof Date ? value : new Date(value as string | number);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    ...(opts.seconds ? { second: '2-digit' as const } : {}),
    hour12: opts.hour12,
    timeZone: opts.timeZone,
  });
}

/**
 * Whole days from `from` to `to`, both `YYYY-MM-DD`.
 *
 * Reckoned on the bare triples through `Date.UTC` with no zone in it at all —
 * the same trick the report presets use, and the reason a DST boundary cannot
 * move a boundary day. Negative when `to` is before `from`.
 *
 * It takes STRINGS and never reads a clock: whose "today" is being counted from
 * is the caller's decision, and on this product that answer is always the
 * COMPANY's day (docs/time-and-dates.md), never the browser's.
 */
export function daysBetweenYmd(from: string, to: string): number {
  const a = from.split('-').map(Number);
  const b = to.split('-').map(Number);
  if (a.length !== 3 || b.length !== 3 || a.some(Number.isNaN) || b.some(Number.isNaN)) return 0;
  const ms = Date.UTC(b[0]!, b[1]! - 1, b[2]!) - Date.UTC(a[0]!, a[1]! - 1, a[2]!);
  return Math.round(ms / 86_400_000);
}

/**
 * The zone's offset from UTC, in milliseconds, AT a given instant.
 *
 * Read the wall clock `timeZone` shows at `at`, treat those digits as if they
 * were UTC, and subtract the real instant. What is left is the offset — the
 * only way to get one out of `Intl` without a date library.
 */
function zoneOffsetMs(timeZone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const at_ = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');
  // `hour` comes back as 24 for midnight under some ICU versions; `% 24` is the
  // documented guard, and it is safe because the day part is already resolved.
  const asIfUtc = Date.UTC(
    at_('year'),
    at_('month') - 1,
    at_('day'),
    at_('hour') % 24,
    at_('minute'),
    at_('second')
  );
  return asIfUtc - at.getTime();
}

/**
 * THE INSTANT A PERSON MEANT when they typed a date and a time.
 *
 * The counterpart to `ymdInZone`, and the one this file was missing. A wall
 * clock a user types into an attendance form is a COMPANY-ZONE reading, because
 * that is the clock the whole product records and displays attendance on
 * (`docs/time-and-dates.md` §1 — the system clock decides every boundary, and
 * anything that can reach a payslip is measured on it).
 *
 * THE DEFECT THIS EXISTS FOR. The corrections form composed its timestamp with
 * `new Date(\`${date}T${time}:00\`)`, which parses in the DEVICE zone. Every
 * screen then formatted the result in the COMPANY zone, so the two disagreed by
 * the difference between them — four hours for a browser on UTC. A worker in
 * Dubai typing 15:00 saw their own request come back reading 19:00, and typing
 * anything inside the last four hours was refused as "in the future", which is
 * exactly the window where a missed punch is noticed. Device-zone parsing is
 * wrong by coincidence: it agrees with the company only when the reader happens
 * to sit where the company does, so it passes every test written at a desk in
 * Dubai.
 *
 * Offsets are read twice on purpose. The first offset is sampled at the naive
 * guess, which can sit on the wrong side of a DST transition; re-reading at the
 * corrected instant settles it. The Gulf has no DST, so this costs nothing
 * there and keeps the helper honest for a tenant that does.
 *
 * @param timeZone the company zone; coerced through `safeTimeZone`
 * @param ymd      `YYYY-MM-DD` as the person read it on the form
 * @param hm       `HH:MM` on that same clock
 */
export function instantFromZonedWallClock(
  timeZone: string | null | undefined,
  ymd: string,
  hm: string
): Date {
  const tz = safeTimeZone(timeZone);
  const [y, m, d] = ymd.split('-').map(Number);
  const [hh, mi] = hm.split(':').map(Number);
  if ([y, m, d, hh, mi].some((n) => n === undefined || Number.isNaN(n))) {
    return new Date(NaN);
  }
  const guess = Date.UTC(y as number, (m as number) - 1, d as number, hh as number, mi as number);
  const firstPass = guess - zoneOffsetMs(tz, new Date(guess));
  return new Date(guess - zoneOffsetMs(tz, new Date(firstPass)));
}
